import React from "react"

import { ChevronDown, KeyRound, LogOut, Upload, Users } from "lucide-react"
import { useNavigate } from "react-router-dom"

import ChangePasswordDialog from "@/components/ChangePasswordDialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/contexts/AuthContext"

/**
 * Standalone replacement for the resourcing portal's TopNavigation +
 * UserDropdown. This tool has a single section, so the top bar carries the
 * product name and an account menu rather than a link list.
 *
 * Colours are the same bg-secondary (Dark Jungle Green) nav with white text
 * that TopNavigation uses, rather than a generic white header — this is the
 * one piece of chrome present on every screen, so it is what makes the app
 * read as HIVED rather than as an unbranded admin tool.
 *
 * The admin screens live in the account menu rather than as extra tabs: the
 * tabs are the day-to-day workflow, and administering who can sign in or
 * re-pushing a route to Samsara is neither day-to-day nor something most users
 * can do at all.
 */
const CollectionsLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, isPartner, signOut } = useAuth()
  const navigate = useNavigate()
  const [changingPassword, setChangingPassword] = React.useState(false)
  const displayName = user?.full_name ?? user?.email ?? ""

  return (
    <div className="flex h-[var(--app-vh)] flex-col overflow-hidden bg-gray-50">
      <header className="sticky top-0 z-50 flex flex-shrink-0 items-center justify-between border-b border-primary/20 bg-secondary px-6 py-3 shadow-md">
        <button
          type="button"
          onClick={() => navigate("/collections/monitoring")}
          className="flex items-baseline gap-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <span className="text-sm font-semibold text-white">Collections Planning</span>
          <span className="text-xs text-primary">HIVED</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-white hover:bg-white/10 hover:text-white"
            >
              <span className="max-w-[220px] truncate">{displayName}</span>
              <ChevronDown size={14} className="text-white/60" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="truncate text-sm font-medium text-foreground">{displayName}</div>
              <div className="truncate text-xs text-muted-foreground">
                {user?.email}
                {isAdmin && <span className="ml-1 text-muted-foreground">· admin</span>}
                {/* Whose routes they are looking at, since nothing else on screen says the board
                    is one company's and not the whole operation. */}
                {isPartner && (
                  <span className="ml-1 text-muted-foreground">· {user?.operator}</span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isAdmin && (
              <>
                <DropdownMenuItem onSelect={() => navigate("/admin/access")} className="gap-2">
                  <Users size={14} aria-hidden="true" />
                  Manage access
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/admin/exports")} className="gap-2">
                  <Upload size={14} aria-hidden="true" />
                  Route exports
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {/* Only a partner has a password here; everyone else's is Google's to manage. */}
            {isPartner && (
              <DropdownMenuItem onSelect={() => setChangingPassword(true)} className="gap-2">
                <KeyRound size={14} aria-hidden="true" />
                Change password
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void signOut()} className="gap-2">
              <LogOut size={14} aria-hidden="true" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</main>
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </div>
  )
}

export default CollectionsLayout
